import DTOExperssion = namaTypes.DTOExperssion;
import BaseEntityDTO = namaTypes.BaseEntityDTO;
import DTOCriteria = namaTypes.DTOCriteria;
import ExpressionRelationship from "@/utils/constants/ExpressionRelationship.ts";
import CriteriaBuilder from "@/utils/criteria/CriteriaBuilder.ts";
import Operator from "@/utils/constants/Operator.ts";
import CommonFieldIds from "@/utils/constants/CommonFieldIds.ts";
import CriteriaUtils from "@/utils/criteria/CriteriaUtils.ts";
import {AddIsNullIfNeeded} from "@/utils/UIConstants/enums.ts";

export default class ExpressionBuilder
{
  expression: DTOExperssion;
  criteriaBuilder: CriteriaBuilder

  constructor(criteriaBuilder: CriteriaBuilder, dummy?: boolean) {
    this.expression = {};
    this.criteriaBuilder = criteriaBuilder;
    this.relationship(ExpressionRelationship.AND);
    if (!dummy)
      criteriaBuilder.criteria.expressions!.push(this.expression);
    criteriaBuilder.lastExpression = this;
  }

  relationship(relationship: string) {
    this.expression.relation = relationship;
    return this;
  }

  field(id: string) {
    if (this.expression.field)
      throw new Error("field can be only called once on an expression");
    this.expression.field = id;
    return this;
  }

  openBracket() {
    this.expression.operator = Operator.OpenBracket;
    return new ExpressionBuilder(this.criteriaBuilder);
  }

  closeBracket() {
    let expressionBuilder = new ExpressionBuilder(this.criteriaBuilder);
    expressionBuilder.expression.operator = Operator.CloseBracket;
    return expressionBuilder;
  }

  equal(value: any): ExpressionBuilder {
    return this.equal0(value, AddIsNullIfNeeded.No);
  }

  equal0(value: any, addIsNullIfNeeded: AddIsNullIfNeeded) {
    if (addIsNullIfNeeded == AddIsNullIfNeeded.Yes && !value)
      return this.openBracket().field(this.expression.field!).equal(value).or().field(this.expression.field!).equal(null).closeBracket();
    else {
      this.expression.operator = Operator.Equal;
      if (value && typeof value === 'object' && !(value as BaseEntityDTO)['lockVersion']) {
        this.expression.field = this.expression.field + ".id";
        value = (value as BaseEntityDTO).id;
      }
      this.expression.rightHandSide = value;
    }
    return this;
  }

  isNull() {
    return this.equal(null);
  }

  isNotNull() {
    return this.notEqual(null);
  }

  notEqual(value) {
    this.expression.operator = Operator.NotEqual;
    this.expression.rightHandSide = value;
    return this;
  }

  lessThan(value) {
    this.expression.operator = Operator.LessThan;
    this.expression.rightHandSide = value;
    return this;
  }

  greaterThan(value) {
    this.expression.operator = Operator.GreaterThan;
    this.expression.rightHandSide = value;
    return this;
  }

  lessThanOrEqual(value) {
    this.expression.operator = Operator.LessThanOrEqual;
    this.expression.rightHandSide = value;
    return this;
  }

  greaterThanOrEqual(value) {
    this.expression.operator = Operator.GreaterThanOrEqual;
    this.expression.rightHandSide = value;
    return this;
  }

  contains(value) {
    this.expression.operator = Operator.Contains;
    this.expression.rightHandSide = value;
    return this;
  }

  operator(operator: string) {
    this.expression.operator = operator;
    return this;
  }

  startsWith(value) {
    this.expression.operator = Operator.StartsWith;
    this.expression.rightHandSide = value;
    return this;
  }

  notStartsWith(value) {
    this.expression.operator = Operator.NotStartsWith;
    this.expression.rightHandSide = value;
    return this;
  }

  endsWith(value) {
    this.expression.operator = Operator.EndsWith;
    this.expression.rightHandSide = value;
    return this;
  }

  notEndWith(value) {
    this.expression.operator = Operator.NotEndWith;
    this.expression.rightHandSide = value;
    return this;
  }

  notContains(value) {
    this.expression.operator = Operator.NotContain;
    this.expression.rightHandSide = value;
    return this;
  }

  in(...values) {
    this.expression.operator = Operator.In;
    this.expression.inValues = values;
    return this;
  }

  notIn(...values) {
    this.expression.operator = Operator.NotIn;
    this.expression.inValues = values;
    return this;
  }

  or() {
    this.relationship(ExpressionRelationship.OR);
    return new ExpressionBuilder(this.criteriaBuilder);
  }

  and() {
    this.relationship(ExpressionRelationship.AND);
    return new ExpressionBuilder(this.criteriaBuilder);
  }

  build() {
    return this.criteriaBuilder.build();
  }

  fieldIn(fieldId: string, ...args) {
    if (args.length > 0) {
      if (args.length > 1)
        return this.criteriaBuilder.lastExpression!.field(fieldId).in(args);
      else
        return this.criteriaBuilder.lastExpression!.field(fieldId).equal(args[0]);
    }
    return this.criteriaBuilder.lastExpression;
  }

  commitedBefore() {
    return this.field(CommonFieldIds.COMMITE_BEFORE).equal(true);
  }

  idNotEqual(id) {
    return this.field(CommonFieldIds.ID).notEqual(id);
  }

  idNotIn(ids: any[]) {
    return this.field(CommonFieldIds.ID).notIn(ids);
  }

  idIn(ids: any[]) {
    return this.field(CommonFieldIds.ID).in(ids);
  }

  join(crt: DTOCriteria) {
    this.criteriaBuilder.criteria = CriteriaUtils.joinCriteria(this.criteriaBuilder.criteria, crt);
    return this;
  }
}
